import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Button,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Box,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import { Close as CloseIcon, Download as DownloadIcon } from '@mui/icons-material';
import { merchantsService } from '@/superadmin/services/merchantsService';
import type { KycData } from '@/types/kycdata';
import { notify } from '@/components/common/ToastNotification';

const steps = ['Basic Details', 'Business Details', 'Bank Details'];

const statusOptions = [
  { value: 0, label: 'Pending' },
  { value: 1, label: 'In Review' },
  { value: 2, label: 'Approved' },
  { value: 3, label: 'Rejected' },
];

const KycDrawer: React.FC<KycDrawerProps> = ({ merchantId, open, onClose }) => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [kycData, setKycData] = useState<KycData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [basicStatus, setBasicStatus] = useState<number>(0);
  const [businessStatus, setBusinessStatus] = useState<number>(0);
  const [bankStatus, setBankStatus] = useState<number>(0);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [reason, setReason] = useState<string>('');
  const [pendingStatus, setPendingStatus] = useState<number | null>(null);
  const [pendingUuid, setPendingUuid] = useState<string>('');
  const [pendingType, setPendingType] = useState<string>('');

  useEffect(() => {
    if (open) fetchKycData();
    else {
      setKycData(null);
      setActiveStep(0);
    }
  }, [open, merchantId]);

  useEffect(() => {
    if (kycData) {
      setBasicStatus(kycData.basic?.status ?? 0);
      setBusinessStatus(kycData.business?.status ?? 0);
      setBankStatus(kycData.bank?.status ?? 0);
    }
  }, [kycData]);

  const fetchKycData = async () => {
    setLoading(true);
    try {
      const res = await merchantsService.getMerchantKyc(merchantId);
      setKycData(res?.data || {});
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const entityTypes: Record<string, string> = {
    '1': 'Individual/Freelancers',
    '2': 'Sole Proprietor',
    '3': 'Partnership Firm',
    '4': 'Private Ltd/Public Ltd/OPC',
    '5': 'LLP',
    '6': 'Trust – NGO, Temples, Others',
    '7': 'Society/Unincorporated associations/BOI',
    '8': 'HUF',
    '9': 'Government Entity',
    '10': 'Co-operative Credit Society/JV/AOP',
  };

  const labelCellSx = { fontWeight: 700, width: '40%', color: 'text.secondary', textTransform: 'uppercase', fontSize: 16, letterSpacing: 0.5 };
  const valueCellSx = { fontWeight: 500, fontSize: 16, color: 'text.primary' };

  const InfoTable = ({ rows }: { rows: { label: string; value?: string }[] }) => (
    <Table size="small" sx={{ mb: 2 }}>
      <TableBody>
        {rows.map(({ label, value }) => (
          <TableRow key={label}>
            <TableCell sx={labelCellSx}>{label}</TableCell>
            <TableCell sx={valueCellSx}>{value || '-'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const handleStatusChange = (status: number, uuid: string, type: string) => {
    if (activeStep === 0) setBasicStatus(status);
    else if (activeStep === 1) setBusinessStatus(status);
    else setBankStatus(status);
    setPendingStatus(status);
    setPendingUuid(uuid);
    setPendingType(type);
    setConfirmOpen(true);
  };

  const handleConfirmStatus = async () => {
    if (pendingStatus === null || !pendingUuid || !pendingType) {
      notify.error('Missing required data');
      return;
    }

    setConfirmOpen(false);

    try {
      const payload = {
        kyc_type: pendingType,
        uuid: pendingUuid,
        kyc_status: pendingStatus,
        reason: pendingStatus === 3 ? reason : ''
      };

      const response = await merchantsService.updateKycStatus(payload);

      if (response?.success) {
        notify.success(response?.message || 'Status updated successfully');
        setReason('');
        setPendingStatus(null);
        setPendingUuid('');
        setPendingType('');
      } else {
        notify.error(response?.message || 'Failed to update status');
      }
    } catch (error: any) {
      const errorMsg = error?.response?.message || error?.message || 'Failed to update status';
      notify.error(errorMsg);
    }
  };

  const handleCancelConfirm = () => {
    if (activeStep === 0) setBasicStatus(basicStatus);
    else if (activeStep === 1) setBusinessStatus(businessStatus);
    else setBankStatus(bankStatus);
    setConfirmOpen(false);
    setReason('');
    setPendingStatus(null);
    setPendingUuid('');
    setPendingType('');
  };

  const renderStepContent = () => {
    if (!kycData) return null;

    const { basic = {}, business = {}, bank = {}, merchant = {} } = kycData;

    const docLinks: { href?: string; label: string }[] = [
      { href: business?.business_proof, label: 'Business Proof' },
      { href: business?.certificate_of_incorporation, label: 'Certificate of Incorporation' },
      { href: business?.company_pan_document, label: 'Company Pan Document' },
      { href: business?.gst_certificate, label: 'GST Certificate' },
      { href: business?.directors_identity_proof, label: 'Directors Identity Proof' },
      { href: business?.board_resolution, label: 'Board Resolution' },
      { href: business?.aadhaar_document, label: 'Aadhaar Document' },
      { href: business?.declaration_of_beneficial_ownership, label: 'Declaration of Beneficial Ownership' },
    ];

    switch (activeStep) {
      case 0:
        return (
          <Box>
            <InfoTable rows={[
              { label: 'Name', value: merchant?.name },
              { label: 'Email', value: merchant?.email },
              { label: 'Phone Number', value: basic?.phone_number },
              { label: 'Company Name', value: basic?.company_name },
              { label: 'Entity Type', value: entityTypes[basic?.entity_type] || basic?.entity_type },
              { label: 'Pincode', value: basic?.pincode },
              { label: 'State', value: basic?.state },
              { label: 'City', value: basic?.city },
              { label: 'Address', value: basic?.address },
            ]} />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={basicStatus} onChange={(e) => handleStatusChange(e.target.value as number, basic?.uuid || '', 'basic')} label="Status">
                {statusOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        );

      case 1:
        return (
          <Box>
            <InfoTable rows={[
              { label: 'GST Number', value: business?.gstin },
              { label: 'PAN Number', value: business?.pan },
              { label: 'Aadhaar Number', value: business?.aadhaar_number },
              { label: 'Business Name', value: business?.business_name },
            ]} />
            <Table size="small" sx={{ mb: 2 }}>
              <TableBody>
                {docLinks.filter(d => d.href).map(({ href, label }) => (
                  <TableRow key={label}>
                    <TableCell sx={labelCellSx}>{label}</TableCell>
                    <TableCell>
                      <Link href={href} target="_blank" download sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DownloadIcon fontSize="small" /> Download
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={businessStatus} onChange={(e) => handleStatusChange(e.target.value as number, business?.uuid || '', 'business')} label="Status">
                {statusOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        );

      case 2:
        return (
          <Box>
            <InfoTable rows={[
              { label: 'Bank Name', value: bank?.bank_name },
              { label: 'IFSC Code', value: bank?.ifsc },
              { label: 'Account Number', value: bank?.merchant_bank_number },
            ]} />
            {bank?.bank_proof && (
              <Table size="small" sx={{ mb: 2 }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={labelCellSx}>Bank Proof</TableCell>
                    <TableCell>
                      <Link href={bank.bank_proof} target="_blank" download sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DownloadIcon fontSize="small" /> Download
                      </Link>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={bankStatus} onChange={(e) => handleStatusChange(e.target.value as number, bank?.uuid || '', 'bank')} label="Status">
                {statusOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose}>
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 12, right: 12 }}>
          <CloseIcon />
        </IconButton>
        <Box
          sx={{ width: 1200, height: '100%', display: 'flex', flexDirection: 'column', p: 3, m: 4 }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Merchant KYC
          </Typography>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label, index) => (
              <Step key={label} onClick={() => setActiveStep(index)}>
                <StepLabel sx={{ cursor: 'pointer' }}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 6 }}>
            {loading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  gap: 4,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              renderStepContent()
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button
              variant="outlined"
              disabled={activeStep === 0}
              onClick={() => setActiveStep(prev => prev - 1)}
            >
              Previous Step
            </Button>
            <Button
              variant="contained"
              disabled={activeStep === steps.length - 1}
              onClick={() => setActiveStep(prev => prev + 1)}
            >
              Next Step
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Dialog open={confirmOpen} onClose={handleCancelConfirm} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Are you sure you want to update the status?</Typography>
          {pendingStatus === 3 && (
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Reason for Rejection"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirm}>No</Button>
          <Button onClick={handleConfirmStatus} variant="contained" disabled={pendingStatus === 3 && !reason.trim()}>Yes</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default KycDrawer;
